import { Divider, Stack, Text } from '@ltvco/refresh-lib/theme';
import { calculateTimeSince } from '../AnswerDetails/utils';
import { SocialSentiment } from 'api/requests/socialSentiments/interfaces';
import { getSocialSentimentTitle } from '../utils';
import { useCallback } from 'react';
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { SOCIAL_SENTIMENT_GA4_EVENTS } from '../constants';
import { ChatBubbleOutline, VisibilityOutlined } from '@mui/icons-material';

interface EntryDetailsProps {
  isModal?: boolean;
  item?: SocialSentiment;
  onOpenQADisplayModal?: () => void;
  setSelectedEntry?: (entry?: SocialSentiment) => void;
}

export const EntryDetails = ({
  isModal = false,
  item,
  onOpenQADisplayModal,
  setSelectedEntry,
}: EntryDetailsProps) => {
  const { trackEventGA4 } = useAppConfigContext();
  const hasItem = Boolean(item);
  const sourceLabel = item?.source_name ?? 'Bumper AI';
  const hasSourceLink = Boolean(item?.source_url);
  const title = item ? getSocialSentimentTitle(item) : '';
  const commentCount = item?.comments_count ?? 0;

  const handleOpenQADisplayModal = () => {
    if (!item) {
      return;
    }
    onOpenQADisplayModal?.();
    setSelectedEntry?.(item);
  };

  const handleOpenSourceLink = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      trackEventGA4({
        event_name:
          SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentClickUrlAction.name,
        type: SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentClickUrlAction.type,
      });
      event.stopPropagation();
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!hasItem) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenQADisplayModal();
      }
    },
    [handleOpenQADisplayModal, hasItem],
  );

  return (
    <Stack width="100%">
      <Stack
        width="100%"
        direction="row"
        spacing={2}
        alignItems="flex-start"
        onClick={handleOpenQADisplayModal}
        onKeyDown={handleKeyDown}
        role={hasItem ? 'button' : undefined}
        tabIndex={hasItem ? 0 : -1}
        aria-disabled={!hasItem}
        sx={{ cursor: hasItem ? 'pointer' : 'default' }}
      >
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          {hasItem ? (
            <Text variant="body2">
              <strong>{title}:</strong> {item?.summary_md}
            </Text>
          ) : null}
          <Stack
            direction={isModal ? 'column' : { xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={
              isModal ? 'flex-start' : { xs: 'flex-start', sm: 'center' }
            }
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="body2" color="text.secondary">
                {calculateTimeSince(
                  item?.display_timestamp || item?.published_at,
                )}
              </Text>
              <Text variant="body2" color="text.secondary">
                •{' '}
                {hasSourceLink ? (
                  <Text
                    component="a"
                    href={item?.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleOpenSourceLink}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textDecoration: 'underline',
                    }}
                  >
                    {sourceLabel}
                  </Text>
                ) : (
                  sourceLabel
                )}
              </Text>
            </Stack>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ ml: 'auto' }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ChatBubbleOutline
                  sx={{ fontSize: '1rem', color: 'text.secondary' }}
                />
                <Text variant="body2" color="text.secondary">
                  {commentCount}
                </Text>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <VisibilityOutlined
                  sx={{ fontSize: '1rem', color: 'text.secondary' }}
                />
                <Text variant="body2" color="text.secondary">
                  {item?.views ?? 0}
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ mt: 2 }} />
    </Stack>
  );
};
